import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CTASection } from '../components/sections';
import { 
  GraduationCap,
  Users,
  Lightbulb,
  Target,
  BookOpen,
  Presentation,
  School,
  Briefcase,
  CheckCircle2,
  ArrowRight,
  Network,
  Zap
} from 'lucide-react';

const PROGRAMS = [
  {
    id: 'telecom-fundamentals',
    icon: BookOpen,
    title: 'Telecom Fundamentals',
    description: 'Introduction to telecommunications concepts, covering the basics of how voice and data travel across networks.',
    audience: 'High school & University students',
    duration: '2-4 hours'
  },
  {
    id: 'fiber-optics',
    icon: Zap,
    title: 'Fiber Optics Demonstrations',
    description: 'Hands-on sessions showing how fiber optic cables work, including splicing demonstrations and light transmission.',
    audience: 'Technical students',
    duration: '3-5 hours'
  },
  {
    id: 'infrastructure',
    icon: Network,
    title: 'Infrastructure Concepts',
    description: 'Understanding the physical layer of telecommunications: towers, cables, data centers, and distribution networks.',
    audience: 'Engineering students',
    duration: '4-6 hours'
  },
  {
    id: 'career-guidance',
    icon: Briefcase,
    title: 'Career Pathway Guidance',
    description: 'Exploring career opportunities in telecommunications, from field technicians to network engineers.',
    audience: 'All students',
    duration: '1-2 hours'
  }
];

const BENEFITS = [
  { id: 'curiosity', icon: Lightbulb, title: 'Spark Curiosity', description: 'Inspire the next generation with real-world telecommunications knowledge' },
  { id: 'skills', icon: Target, title: 'Practical Skills', description: 'Hands-on demonstrations that bring theory to life' },
  { id: 'connections', icon: Users, title: 'Industry Connections', description: 'Bridge the gap between academia and industry' },
  { id: 'readiness', icon: GraduationCap, title: 'Career Readiness', description: 'Prepare students for rewarding careers in telecom' }
];

const PARTNERSHIP_FEATURES = [
  { id: 'seminar-dev', text: 'Custom seminar development' },
  { id: 'schedules', text: 'Recurring program schedules' },
  { id: 'speakers', text: 'Guest speaker sessions' },
  { id: 'lab', text: 'Lab equipment demonstrations' },
  { id: 'mentorship', text: 'Industry mentorship opportunities' }
];

const EducationHero = () => (
  <section className="relative py-20 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0f1d32] to-[#162544]" />
    <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#10b981]/10 rounded-full blur-3xl" />
    
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-[#10b981]/20 flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-[#10b981]" />
          </div>
          <span className="text-[#10b981] font-medium">Education Programs</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-[#f1f5f9] mb-6 animate-fade-in-up">
          Inspiring Future Telecom Professionals
        </h1>
        <p className="text-xl text-[#94a3b8] leading-relaxed animate-fade-in-up delay-100">
          We introduce young people to the world of telecommunications through engaging 
          seminars, hands-on demonstrations, and career guidance programs.
        </p>
      </div>
    </div>
  </section>
);

const BenefitsSection = () => (
  <section className="py-16 bg-[#0f1d32]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {BENEFITS.map((benefit) => (
          <div 
            key={benefit.id}
            className="bg-[#162544] rounded-xl p-6 border border-[#1e3a5f]/50 card-hover"
          >
            <div className="w-12 h-12 rounded-lg bg-[#10b981]/20 flex items-center justify-center mb-4">
              <benefit.icon className="w-6 h-6 text-[#10b981]" />
            </div>
            <h3 className="text-lg font-semibold text-[#f1f5f9] mb-2">{benefit.title}</h3>
            <p className="text-sm text-[#94a3b8]">{benefit.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ProgramsSection = () => (
  <section className="py-20 bg-[#0a1628]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#f1f5f9] mb-4">Our Programs</h2>
        <p className="text-[#94a3b8] max-w-2xl mx-auto">
          Tailored educational experiences for different audiences and learning objectives
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {PROGRAMS.map((program) => (
          <div 
            key={program.id}
            className="bg-[#0f1d32] rounded-2xl p-8 border border-[#1e3a5f]/50 card-hover"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 rounded-xl bg-[#10b981]/20 flex items-center justify-center flex-shrink-0">
                <program.icon className="w-7 h-7 text-[#10b981]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#f1f5f9] mb-1">{program.title}</h3>
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="text-[#10b981]">{program.audience}</span>
                  <span className="text-[#64748b]">•</span>
                  <span className="text-[#94a3b8]">{program.duration}</span>
                </div>
              </div>
            </div>
            <p className="text-[#94a3b8] leading-relaxed">{program.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PartnershipsSection = () => (
  <section className="py-20 bg-[#0f1d32]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row items-center gap-12">
        <div className="lg:w-1/2">
          <div className="flex items-center gap-3 mb-4">
            <School className="w-6 h-6 text-[#10b981]" />
            <span className="text-[#10b981] font-medium">Partnerships</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#f1f5f9] mb-6">
            University & School Partnerships
          </h2>
          <p className="text-[#94a3b8] mb-6 leading-relaxed">
            We work closely with educational institutions to deliver customized seminars 
            that complement existing curricula and provide real-world industry insights.
          </p>
          <ul className="space-y-4 mb-8">
            {PARTNERSHIP_FEATURES.map((item) => (
              <li key={item.id} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#10b981] flex-shrink-0" />
                <span className="text-[#f1f5f9]">{item.text}</span>
              </li>
            ))}
          </ul>
          <Link to="/contact">
            <Button className="btn-primary text-white font-medium px-8 py-4 rounded-xl">
              Partner With Us
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
        <div className="lg:w-1/2 flex justify-center">
          <div className="w-80 h-80 rounded-3xl bg-[#162544] border border-[#1e3a5f]/50 flex items-center justify-center">
            <Presentation className="w-32 h-32 text-[#10b981]/50" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Education = () => {
  return (
    <div className="min-h-screen bg-[#0a1628]">
      <EducationHero />
      <BenefitsSection />
      <ProgramsSection />
      <PartnershipsSection />
      <CTASection
        title="Bring Telecom Education to Your Institution"
        description="Contact us to discuss how we can inspire and educate your students about the exciting world of telecommunications."
        actionLabel="Schedule a Seminar"
        actionTo="/contact"
        accentColor="#10b981"
      />
    </div>
  );
};

export default Education;
